import { toast, aliareProducts } from '@/common/util'
import baseApi from './base.api'
import { plantarApiV2 } from '@/config/appAPIs'

import type { ContratoType, SubscriptionDateType } from '@/types/types'

interface AliareOwner {
  active: boolean
  blocked: boolean
  description: string
  phoneMobile: string | null
  phoneFixed: string | null
  segmentId: string
  createdAt: Date
  lastUpdateAt: Date | null
  birthDate: Date | null
  notificationSMS: boolean
  notificationPush: boolean
  isTenantOwner: boolean
  id: string
  legalName: string
  userId: string
  email: string | null
  picture: string | null
  viewLinkPicture: string | null
  typePerson: string
  identificationDocument: string
}

interface AliareUser {
  id: string
  legalName: string
  personId: string
  person: string
  email: string
  emailConfirmed: boolean
  adminUser: boolean
  financeUser: boolean
  produto: string | null
  status: boolean
  block: boolean
  active: boolean
  identificationDocument: string
  typePerson: string
  createdAt: Date
  lastUpdatedAt: Date | null
  picture: string | null
  viewLinkPicture: string | null
}

interface TenantResult {
  owner: AliareOwner
  principalAdminUser: {
    isPrincipal: boolean
    userId: string
    tenantId: string
    user: AliareUser
    tenant: {
      id: string
      description: string
      active: boolean
    }
  }
  adminUsers: {
    isPrincipal: boolean
    userId: string
    tenantId: string
    user: AliareUser
    tenant: {
      id: string
      description: string
      active: boolean
    }
  }[]
  blocked: boolean
  isAliareTenant: boolean
  createdAt: Date
  finality: string
  typeAccount: string
  identifiedOwner: boolean
  tenantPartners: {
    isPrincipal: boolean
    tenant: {
      id: string
      description: string
      active: boolean
    }
    partner: AliareUser
    id: string
    blocked: boolean
    active: boolean
  }[]
  id: string
  description: string
  active: boolean
}

export interface ContractRow {
  id: string
  personId: string
  agbAccountCode: number
  expirationDate: Date | null
  contractDate: Date | null
  observation: string
  usersQuantity: number
  diskSpace: number
  coursesRecordsLimit: number
  trailsRecordsLimit: number
  file: string | null
  responsibleName: string
  responsibleEmail: string | null
  responsibleCellphone: string
  defaultAccessUrl: string
  customAccessUrl: string
  communicationEmail: string | null
  managerPersonType: string
  managerCpf: string
  managerEmail: string
  managerName: string
  managerPersonId: string
  managerUserId: string
  contractTenantId: string
  owner: null | string
  bunnyVideoCollectionId: string
  active: boolean
  companyName: string
  aliareIdentificationDocumentPeople: string
  aliareTypePeople: string
  activeUsersQuantity: number
  sectionsIds: string[]
  aliareSubscriptionId: string
  createdAt: Date
  clientClassification: string
  projectStatus: string
  companyId: string
  products: string | string[]
  communicationsContracts: {
    id: string
    communicationId: string
    contractId: string
  }[],
  descriptionAccount: string
}

export const contratoService = {
  ...baseApi,
  resource: 'Contrato',
  getAll(opt?: {
    search?: string
    //
    name?: string
    active?: boolean
    code?: string
    types?: string[]
    competenceIds?: string[]
    categoryIds?: string[]
    //
    page?: number
    size?: number
    orderBy?: { prop: string, order: 'ASC' | 'DESC' }[]
  }, options?: any) {
    opt = opt || {}

    const paramsDictionary = {
    }

    const orderDictionary = {
    }

    const extraData = {}

    Object.keys(opt).forEach((key) => {
      if (typeof paramsDictionary[key] !== 'undefined' && typeof opt[key] !== 'undefined' && opt[key] !== null)
        extraData[paramsDictionary[key]] = opt[key]
    })

    // Ordenação
    if (opt.orderBy?.length) {
      extraData['Ordenacao'] = opt.orderBy.map((item) => `${orderDictionary[item.prop]}${item.order === 'DESC' ? 'Desc' : 'Asc'}`)
    }

    return contratoService
      .get<{
        id: string
        pessoaId: string
        codigoContaAGB: number
        dataDeValidade: Date
        dataDeContrato: Date
        observacao: string
        quantidadeDeUsuariosContratados: number
        espacoEmDiscoContratado: number
        limiteDeRegistrosDeCursos: number
        limiteDeRegistroDeFormacoes: number
        arquivo: string | null
        nomeDoResponsavel: string
        emailDoResponsavel: null,
        celularDoResponsavel: string
        urlDeAcessoPadrao: string
        urlDeAcessoPersonalizado: string
        emailDeComunicacaoUsadoPelaEmpresa: string | null
        tipoPessoaGestor: string
        cpfGestor: string
        emailGestor: string
        nomeGestor: string
        personIdGestor: string
        userIdGestor: string
        tenantIdDoContrato: string
        owner: null,
        bunnyVideoCollectionId: string
        active: true,
        nomeEmpresa: string
        aliareIdentificationDocumentPeople: string
        aliareTypePeople: string
        qtdeUsuarioAtivos: number
        secoesIds: string[]
        aliareSubscriptionId: string
        createdAt: Date
        classificacaoCliente: string
        statusProjeto: string
        empresaId: string
        produtos: string
        contratosComunicacoes: any[],
        descricaoConta: string
      }[]>('', extraData,  options)
      .then(({ success, data }) => {
        return {
          success,
          // Normaliza a extrutura recebida, para o formato local
          data: data.map((item) => ({
            id: item.id,
            personId: item.pessoaId,
            agbAccountCode: item.codigoContaAGB,
            expirationDate: item.dataDeValidade ? new Date(item.dataDeValidade) : null,
            contractDate: item.dataDeContrato ? new Date(item.dataDeContrato) : null,
            observation: item.observacao,
            usersQuantity: item.quantidadeDeUsuariosContratados,
            diskSpace: item.espacoEmDiscoContratado,
            coursesRecordsLimit: item.limiteDeRegistrosDeCursos,
            trailsRecordsLimit: item.limiteDeRegistroDeFormacoes,
            file: item.arquivo,
            responsibleName: item.nomeDoResponsavel,
            responsibleEmail: item.emailDoResponsavel,
            responsibleCellphone: item.celularDoResponsavel,
            defaultAccessUrl: item.urlDeAcessoPadrao,
            customAccessUrl: item.urlDeAcessoPersonalizado,
            communicationEmail: item.emailDeComunicacaoUsadoPelaEmpresa,
            managerPersonType: item.tipoPessoaGestor,
            managerCpf: item.cpfGestor,
            managerEmail: item.emailGestor,
            managerName: item.nomeGestor,
            managerPersonId: item.personIdGestor,
            managerUserId: item.userIdGestor,
            contractTenantId: item.tenantIdDoContrato,
            owner: item.owner,
            bunnyVideoCollectionId: item.bunnyVideoCollectionId,
            active: item.active,
            companyName: item.nomeEmpresa,
            aliareIdentificationDocumentPeople: item.aliareIdentificationDocumentPeople,
            aliareTypePeople: item.aliareTypePeople,
            activeUsersQuantity: item.qtdeUsuarioAtivos,
            sectionsIds: item.secoesIds,
            aliareSubscriptionId: item.aliareSubscriptionId,
            createdAt: new Date(item.createdAt),
            clientClassification: item.classificacaoCliente,
            projectStatus: item.statusProjeto,
            companyId: item.empresaId,
            products: item.produtos
            ? item.produtos.split(',')
                .map((product) => aliareProducts.value[product.trim()] || '-')
                .join(', ')
            : '',
            communicationsContracts: item.contratosComunicacoes?.map((cm) => ({
              id: cm.id,
              communicationId: cm.communicationId,
              contractId: cm.contractId
            })) || [],
            descriptionAccount: item.descricaoConta
          }))
        } as { success: boolean, data: ContractRow[] }
      })
  },
  async isDistribuidor() {
    try {
      return await this.get(`IsDistribuidor`)
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({message: notifications[0] || 'Erro ao verificar informações do usuário!'})
    }
  },
  async getUsageInfo() {
    try {
      return await this.get(`UsageInfo`)
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({message: notifications[0] || 'Erro ao buscar informações do contrato!'})
    }
  },

  /*
   * Endpoints V2
   */

  async create(newContract: ContratoType) {
    try {
      return await plantarApiV2.post(this.resource, {...newContract})
    } catch (error) {
      const { notifications }: { notifications: string[] } = error.response?.data || {}
      toast.error({message: notifications[0] || 'Erro ao criar contrato!'})
    }
  },

  getTenants(options: { ownerId: string; finality?: string; typeAccount?: string }) {
    const { ownerId } = options || {} 

    if (!ownerId) {
      return Promise.reject('ownerId is required')
    }

    delete options.ownerId

    return plantarApiV2.get<TenantResult[]>(`${this.resource}/tenants/${ownerId}`, {
      data: options || {}
    })
  },

  getSubscription(tenantId: string) {
    return plantarApiV2.get<SubscriptionDateType>(`${this.resource}/subscription/${tenantId}`)
  }
}

export default contratoService